const OPEN_INTERVENTION_STATUSES = new Set([
  "planned",
  "confirmed",
  "in_progress",
  "pending",
  "open",
]);

const SERVICE_EVENT_TYPES = new Set([
  "corrective",
  "repair",
  "breakdown",
  "emergency",
]);

const SERVICE_STATUS_RANK = {
  in_progress: 0,
  confirmed: 1,
  planned: 2,
  pending: 3,
  open: 4,
};

export function parseMachineDate(value) {
  if (!value) return null;

  const text = String(value);
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(text)
    ? `${text}T12:00:00`
    : text;
  const date = new Date(normalized);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatMachineDate(value, fallback = "Non renseignée") {
  const date = parseMachineDate(value);

  return date
    ? date.toLocaleDateString("fr-FR", { dateStyle: "medium" })
    : fallback;
}

export function formatMachineDateTime(value, fallback = "Non renseignée") {
  const date = parseMachineDate(value);

  return date
    ? date.toLocaleString("fr-FR", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : fallback;
}

function eventTimestamp(intervention) {
  return (
    parseMachineDate(
      intervention?.scheduledStart ||
        intervention?.createdAt ||
        intervention?.updatedAt
    )?.getTime() || Number.MAX_SAFE_INTEGER
  );
}

export function selectCurrentServiceEvent(interventions = []) {
  return (
    interventions
      .filter(
        (item) =>
          SERVICE_EVENT_TYPES.has(item?.type) &&
          OPEN_INTERVENTION_STATUSES.has(item?.status)
      )
      .sort((left, right) => {
        const statusDifference =
          (SERVICE_STATUS_RANK[left.status] ?? 99) -
          (SERVICE_STATUS_RANK[right.status] ?? 99);

        if (statusDifference !== 0) return statusDifference;
        return eventTimestamp(left) - eventTimestamp(right);
      })[0] || null
  );
}

export function selectNextMaintenance(plans = []) {
  const activePlans = plans.filter(
    (plan) =>
      plan?.isActive !== false &&
      !["inactive", "cancelled", "completed"].includes(plan?.status)
  );

  const withCalendarDate = activePlans
    .filter((plan) => Boolean(plan?.nextDueAt))
    .sort((left, right) => {
      const leftTime = parseMachineDate(left.nextDueAt)?.getTime() ?? Infinity;
      const rightTime = parseMachineDate(right.nextDueAt)?.getTime() ?? Infinity;
      return leftTime - rightTime;
    });

  if (withCalendarDate.length > 0) return withCalendarDate[0];

  return (
    activePlans.find((plan) => plan?.nextDueMeterHours != null) || null
  );
}

export function serviceEventLabel(intervention) {
  if (!intervention) return "Aucun";

  const typeLabels = {
    corrective: "Panne / réparation",
    repair: "Réparation",
    breakdown: "Panne",
    emergency: "Panne urgente",
  };

  return intervention.title || typeLabels[intervention.type] || "Intervention";
}

export function serviceStatusLabel(status) {
  return {
    planned: "Planifiée",
    confirmed: "Confirmée",
    in_progress: "En cours",
    pending: "En attente",
    open: "Ouverte",
  }[status] || status || "";
}

export function maintenanceDetail(plan) {
  if (!plan) return "Aucune maintenance à venir";

  if (plan.nextDueAt) {
    return plan.name || "Maintenance préventive";
  }

  if (plan.nextDueMeterHours != null) {
    return `À ${plan.nextDueMeterHours} h`;
  }

  return "Maintenance préventive";
}
